import { nextFrame } from 'helpers/timing';
import useResetForm from 'stimulus-hooks/use-reset-form';
import ApplicationController from './application_controller';

export default class extends ApplicationController {
  static targets = ['persisted', 'template', 'input', 'resetBtn', 'option'];

  initialize() {
    this.permanentSelection = null;
  }

  connect() {
    useResetForm(this, { element: this.form });
  }

  persistedTargetConnected(target) {
    const { id } = target.dataset;
    if (!id) return;

    this.resetBtnVisibility = false;
    this.permanentSelection = id;
    this.selectListItem(id);
  }

  optionTargetConnected(target) {
    let activated = false;

    if (target.id === this.permanentSelection) {
      target.setAttribute('aria-selected', 'true');
      if (activated) return;

      // Activate first connected option
      if (!this.combobox) return;
      this.combobox.setActive(target);
      activated = true;
    }
  }

  // triggered from `auto-complete:selected`
  add(event) {
    const { id } = event.detail.relatedTarget;
    if (!id) return;

    this.insertToken(id);
    this.resetBtnVisibility = false;
  }

  // triggered from `auto-complete:reset`
  reset() {
    if (this.hasPersistedTarget) {
      this.persistedTargetId = '';
      this.resetBtnVisibility = true;
    }
  }

  // Private

  /**
   * @param {string | number} id
   */
  insertToken(id) {
    if (this.hasPersistedTarget) {
      this.persistedTargetId = id;
    } else {
      const templateClone = this.templateTarget.content.cloneNode(true);
      const hiddenInputField = templateClone.querySelector('[data-behavior="selectHiddenField"]');

      hiddenInputField.value = id;
      this.element.append(templateClone);
    }
  }

  /**
   * @param {string | number} id
   */
  selectListItem(id) {
    this.optionTargets.forEach((item) => {
      if (item.id === id) {
        item.setAttribute('aria-selected', 'true');
        this.inputTarget.value = item.dataset.autocompleteValue;
      }
    });
  }

  resetForm() {
    this.updateSelectionFromText();
  }

  async updateSelectionFromText() {
    await nextFrame();
    const { value } = this.inputTarget;
    if (!value) return;

    this.optionTargets.forEach((option) => {
      const { autocompleteValue } = option.dataset;

      if (autocompleteValue === value) {
        this.resetBtnVisibility = false;
        option.setAttribute('aria-selected', 'true');
        this.insertToken(option.id);
      } else {
        option.setAttribute('aria-selected', 'false');
      }
    });
  }

  /**
   * @param {string | number} value
   */
  set persistedTargetId(value) {
    this.persistedTarget.value = value;
    this.persistedTarget.setAttribute('data-id', value);
  }

  set resetBtnVisibility(value) {
    if (!this.hasResetBtnTarget) return;
    this.resetBtnTarget.hidden = value;
  }

  get form() {
    return this.element.closest('form');
  }

  get combobox() {
    return this.element.autocomplete?.combobox;
  }
}
